import React from 'react';
import { FaShower, FaBed } from 'react-icons/fa6';
import { useContentful } from '../contentful-context';
import { getImageUrl } from '../utils';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { track } from '@vercel/analytics';
import './Models.css';

function ModelFeatures({ features }) {
  const [beds, baths, sqft] = features.split(',');

  return (
    <div className="model-stats">
      <div className="model-stat">
        <div className="model-stat-value">
          <FaBed size={16} className="text-construx-red shrink-0" aria-hidden="true" />
          <span>{beds}</span>
        </div>
        <p className="model-stat-label">Bedrooms</p>
      </div>
      <div className="model-stat">
        <div className="model-stat-value">
          <FaShower size={16} className="text-construx-red shrink-0" aria-hidden="true" />
          <span>{baths}</span>
        </div>
        <p className="model-stat-label">Bathrooms</p>
      </div>
      <div className="model-stat">
        <div className="model-stat-value">
          <span>{sqft}</span>
        </div>
        <p className="model-stat-label">Sq. Ft.</p>
      </div>
    </div>
  );
}

function ModelCard({ model }) {
  const { name, features, type } = model;
  const imageUrl = getImageUrl(model, { width: 480, quality: 82 });
  const navigate = useNavigate();

  const handleModelClick = () => {
    const featuresList = features[0].split(',');
    track('model_card_clicked', {
      modelName: name,
      bedrooms: featuresList[0],
      bathrooms: featuresList[1],
      sqft: featuresList[2],
    });
    navigate(`/models/${name.toLowerCase()}`, { state: { model: name.toLowerCase() } });
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleModelClick();
    }
  };

  return (
    <article
      className="model-card group"
      onClick={handleModelClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="link"
      aria-label={`View ${name} model details`}
    >
      <div className="px-5 pt-5 pb-3 flex items-start justify-between gap-3">
        <h2 className="text-xl font-bold text-warm-900 tracking-tight">{name}</h2>
        {type ? <span className="model-card-type shrink-0">{type}</span> : null}
      </div>

      <div className="model-card-image-wrap">
        <img
          src={imageUrl}
          alt={`${name} exterior render`}
          loading="lazy"
          className="w-full max-w-[280px] h-[200px] object-contain mx-auto"
        />
      </div>

      <ModelFeatures features={features[0]} />

      <button type="button" className="model-card-cta" tabIndex={-1}>
        View {name}
      </button>
    </article>
  );
}

export default function Models() {
  const content = useContentful();

  if (!content.models.length) {
    return null;
  }

  const models = [...content.models].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="models-page font-bai bg-warm-50 min-h-screen">
      <Helmet>
        <title>Models — Construx</title>
        <meta
          name="description"
          content="Browse Construx ADU and home models — one- and two-story designs engineered for precision kit-of-parts construction."
        />
      </Helmet>

      <section className="models-hero">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-14 md:py-20">
          <p className="models-kicker mb-4">Design catalog</p>
          <h1 className="models-title mb-4">Explore our models</h1>
          <p className="models-lede">
            From compact studios to multistory units — every Construx model is designed digitally,
            engineered as a precision envelope system, and ready for your local build partner.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 md:px-12 py-12 md:py-16 pb-20">
        <div className="mb-8 md:mb-10">
          <p className="text-sm font-medium uppercase tracking-widest text-construx-red mb-2">
            {models.length} models
          </p>
          <h2 className="text-2xl md:text-3xl font-bold text-warm-900">
            Pick a starting point
          </h2>
        </div>

        <div className="models-grid">
          {models.map((model) => (
            <ModelCard key={model.name} model={model} />
          ))}
        </div>
      </section>
    </div>
  );
}

export { ModelFeatures };
